import _ from 'lodash'
import styleSelect from 'styleselect'

const headerSelector = '[data-type="organism/header/header"]'
const closeModalBtnSelector = '.js-modal-close'
export const inputsSelector = 'input, textarea, select'

/**
 * Adds a small time delay so that a function is not executed too many times sequentially.
 *
 * @param {Function} callback - The function to delay.
 * @param {number} limit - The time of delay.
 * @returns {Function} The delayed function.
 */
export function throttle( callback, limit ) {
  var wait = false
  return function () {
    if ( !wait ) {
      callback.call()
      wait = true
      setTimeout( function delayFunction() {
        wait = false
      }, limit )
    }
  }
}

/**
 * Checks if the page is rendered in a small device.
 *
 * @returns {boolean}
 */
export function isMobile() {
  return window.innerWidth <= 1024
}

/**
 * Checks if the page is rendered in the styleguide.
 *
 * @returns {boolean}
 */
export function isStyleguide() {
  return document.querySelector( '.js-lean-styleguide' ) !== null
}

/**
 * Gets the offset (top and left) of an element relative to document.
 * 
 * @param {Element} el - The element to calculate offset.
 * @returns {object} Object with offset parameters.
 */
export function offset( el ) {
  const rect = el.getBoundingClientRect()
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  return { top: rect.top + scrollTop, left: rect.left + scrollLeft }
}

/**
 * Locks the body when a modal is opened.
 */
export function lockBody() {
  const scrollY = document.documentElement.style.getPropertyValue( '--scroll-y' )
  const body = document.body
  body.style.position = 'fixed'
  body.style.width = '100%'
  body.style.top = `-${scrollY}`
}

/**
 * Unlocks the body after a modal is closed.
 */
export function unlockBody() {
  const body = document.body
  const scrollY = body.style.top
  body.style.position = ''
  body.style.top = ''
  window.scrollTo( 0, parseInt( scrollY || '0' ) * -1 )
}

/**
* Get IE version.
*
* @return {Number} IE version. 0 if not IE detected.
*/
export function getIEVersion() {
  var sAgent = window.navigator.userAgent
  var Idx = sAgent.indexOf( 'MSIE' )
  
  // If IE, return version number.
  if ( Idx > 0 ) {
    return parseInt( sAgent.substring( Idx + 5, sAgent.indexOf( '.', Idx ) ) )
  } else if ( navigator.userAgent.match( /Trident\/7\./ ) ) { // If IE 11 then look for Updated user agent string.
    return 11
  }

  return 0 //It is not IE.

}

/**
 * Sets the navbar to be transparent or white.
 * 
 * @param {boolean} is_transparent - Turn navbar transparent, defaut is true.
 */
export function navbarTransparent( is_transparent = true ) {
  const headerSelector = '[data-type="organism/header/header"]'
  const header = document.querySelector( headerSelector )
  if ( ! isMobile() && is_transparent ) {
    header.classList.add( 'bg-transparent' )
    header.classList.add( 'text-white' )
    header.classList.remove( 'bg-white' )
    header.classList.remove( 'shadow-lg' )
  } else {
    header.classList.remove( 'bg-transparent' )
    header.classList.remove( 'text-white' )
    header.classList.add( 'bg-white' )
    header.classList.add( 'shadow-lg' )
  }
}

/**
 * Scroll element to view.
 * 
 * @param {Element} target - The element to scroll to.
 */
export function scrollToElement( target ) {
  const targetOffset = offset( target )
  const header = document.querySelector( headerSelector )
  let headerHeight = 0
  if ( header ) {
    headerHeight = header.offsetHeight
  }

  window.scrollTo({
    top: parseInt( targetOffset['top'] || '0' ) - headerHeight,
    left: 0,
    behavior: 'smooth'
  })
}

/**
 * Remove modal.
 */
export function removeModal( modal ) {
  modal.setAttribute( 'aria-opened', false )
  modal.parentNode.removeChild( modal )
}

/**
 * Load and setup HTML inside modal.
 */
export function loadModalHtml( ajax_url, formSetupFn, post_data = {}, query_vars = {} ) {
  return loadJson( ajax_url, post_data, query_vars )
  .then( json => {
    if ( json.success ) {
      const div = document.createElement( 'div' )
      div.innerHTML = json.data.trim()
      const modal = div.firstElementChild
      if ( !modal ) {
        return null
      }

      modal.querySelectorAll( closeModalBtnSelector ).forEach( closeBtn => {
        closeBtn.addEventListener( 'click', e => {
          e.preventDefault()
          removeModal( modal )
        })
      })

      // Style selects.
      setTimeout( () => modal.querySelectorAll( '.select' ).forEach( select => styleSelect( select ) ) )

      // Input validation.
      addInputsValidation( modal.querySelectorAll( inputsSelector ) )

      if ( formSetupFn ) {
        formSetupFn( modal )
      }

      // Show modal.
      const body = document.querySelector( 'body' )
      body.appendChild( modal )
      modal.setAttribute( 'aria-opened', true )

      return modal
    }

    if ( json.data && json.data.msg ) {
      let msg = json.data.msg
      if ( 'User MB data is missing.' === json.data.msg ) {
        msg = 'No MindBody Client data found. Please contact support.'
      }
      throw new Error( msg )
    }
  })
}

/**
 * Add validation to inputs.
 * @param {array} inputs Form inputs to add validation.
 */
export function addInputsValidation( inputs ) {
  _.forEach( inputs, input => {
    input.addEventListener( 'invalid', e => {
      input.classList.add( 'invalid' )
      const errDiv = document.createElement( 'div' )
      errDiv.classList.add( 'js-error' )
      errDiv.innerHTML = input.validationMessage
      input.parentNode.append( errDiv )
    })
  })
}

/**
 * Get JSON from request.
 * @param {string} ajax_action The action to submit.
 * @param {object} post_data The data to submit.
 */
export function loadJson( ajax_action, post_data, query_vars = {} ) {
  let fetch_url = window.lean.ajax_url
  if ( Object.entries( query_vars ).length > 0 ) {
    let url_query_vars = ''
    _.forEach( query_vars, ( value, arg ) => {
      url_query_vars += `${arg}=${value}&`
    })
    fetch_url = `${fetch_url}?${url_query_vars}`
  }

  return fetch( fetch_url, createAjaxData( ajax_action, post_data ) )
  .then( response => {
    if ( response.status === 200 ) {
      return response.json()
    } else {
      throw new Error( `AJAX request Error. '${'favorite'}'` )
    }
  })
  .catch( error => {
    throw new Error( `Form load failed. ${error}` )
  })
}

/**
 * Creates the AJAX requests data.
 *
 * @param {string} action The action to request.
 * @param {array} params The paramters for the action.
 * @return {object} Data for ajax request.
 */
 function createAjaxData( action, params ) {
  params = params ? params : {}
  params.action = action
  const formData = new FormData()

  _.forEach( params, ( value, arg ) => {
    formData.append( arg, value )
  })

  return {
    method: 'POST',
    body: new URLSearchParams(formData),
  }
}

/**
 * Validate form inputs.
 */
export function validateForm ( form ) {
  if ( !form ) {
    return false
  }

  let allValid = true
  const formError = form.querySelector( '.js-form-error' )
  if ( formError ) {
    formError.parentNode.removeChild( formError )
  }

  const inputErrors = form.querySelectorAll( '.js-error' )
  _.forEach( inputErrors, error => {
    error.parentNode.removeChild( error )
  })

  const inputs = form.querySelectorAll( inputsSelector )
  _.forEach( inputs, input => {
    input.classList.remove( 'invalid' )
    if ( !input.checkValidity() ) {
      allValid = false
    }
  })

  return allValid
}

/**
 * Add form error message to element.
 * @param {object} wrapper Element to add error.
 * @param {string} msg Error message.
 */
export function addFormError( wrapper, msg ) {
  if ( !wrapper ) {
    return false
  }

  const div = document.createElement( 'div' )
  div.innerHTML = msg ? msg.trim() : ''
  div.classList.add( 'js-form-error' )
  wrapper.append( div )
}

/**
 * Find element's parent with class.
 * @param {object} startElement Element to start search.
 * @param {string} selector_class Class name to look for.
 * @return {object} Found parent element.
 */
export function findParentElement( startElement, selector_class ) {
  let parentEl = startElement.parentElement
  let found =  null
  while ( parentEl ) {
    if ( parentEl.classList.contains( selector_class ) ) {
      found = parentEl
      break
    }
    parentEl = parentEl.parentElement
  }

  return found
}

/**
 * Crates a Date object from a string of format yyyy-mm-ddThh:mm:ss
 * @param {string} dateValue String date value.
 * @return {Date} The created date object, null on error.
 */
export function getDateObject ( dateValue ) {
  const datetimeParts = dateValue.split( 'T' )
  if ( 2 !== datetimeParts.length ) {
    return null
  }

  const dateParts = datetimeParts[0].split( '-' )
  if ( 3 !== dateParts.length ) {
    return null
  }
  const year = dateParts[0]
  const month = dateParts[1]
  const day = dateParts[2]

  const timeParts = datetimeParts[1].split( ':' )
  if ( 3 !== timeParts.length ) {
    return null
  }
  const hour = timeParts[0]
  const minute = timeParts[1]

  return new Date( year, ( month - 1 ), day, hour, minute, 0, 0 )
}

/**
 * Add minutes to a date object.
 * @param {Date} date Date to modify.
 * @param {int} minutes Number of minutes to add.
 * @return {Date} Modified date.
 */
export function addMinutes( date, minutes ) {
  return new Date( date.getTime() + minutes * 60000 )
}

/**
 * Add leading zero to numbers with one digit.
 * @param {string} number Number to append zero to,
 * @return {string} Number with lading zero.
 */
export function appendLeadingZeroes( number ) {
  if ( number <= 9 ) {
    return `0${ number}`
  }

  return number
}

/**
 * Get a text representation of a date time for use on the Mindbody API.
 * @param {Date} date Date object to get text.
 * @return {string} Date API text.
 */
export function datetimeApi( date ) {
  const year = date.getFullYear()
  const month = appendLeadingZeroes( date.getMonth() + 1 )
  const day = appendLeadingZeroes( date.getDate() )
  const hour = appendLeadingZeroes( date.getHours() )
  const minutes = appendLeadingZeroes( date.getMinutes() )

  return `${year}-${month}-${day}T${hour}:${minutes}:00`
}

/**
 * Hide the mobile book button.
 */
export function hideBookButton() {
  const hiddeInTemplates = [
    '[data-template="templates/mb-locations"]',
    '[data-template="templates/mb-services"]',
    '[data-template="templates/mb-schedule"]',
    '[data-template="templates/mb-enhance"]',
    '[data-template="templates/mb-confirm"]',
    '[data-template="templates/mb-confirm-membership"]',
    '[data-template="templates/mb-confirm-package"]',
    '[data-template="templates/mb-create-account"]',
    '.woocommerce-page',
  ]
  const inHiddeBtnTemplate = document.querySelectorAll( hiddeInTemplates.join( ', ' ) ).length > 0 
  const bookBtn = document.querySelector( '.js-book-button' )

  if ( bookBtn && inHiddeBtnTemplate ) {
    bookBtn.classList.add( 'hidden' )
  }
}

/**
 * Tracked FB pixel event.
 *
 * @param {string} event The event name.
 * @param {object} data The event data.
 */
export function fbSendEvent( event, data ) {
  if ( window.fbq && event && '' !== event && data ) {
    window.fbq( 'track', event, data )
  }
}

/**
 * Tracked GTag event.
 *
 * @param {string} event The event name.
 * @param {object} data The event data.
 */
export function gtagSendEvent( event, data ) {
  if ( window.gtag && event && '' !== event && data ) {
    window.gtag( 'event', event, data )
  }
}

/**
 * Push data object to dataLayer.
 *
 * @param {object} transactionObj The layer data to push.
 */
export function gtagdatalayerPush( transactionObj ) {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push( transactionObj )
} 

/**
 * Send Membership purchase tracking data. Facebook and GA.
 * 
 * @param {object} purchaseId The client specific membership purchase id.
 * @param {object} transactionData The membership name.
 * @param {object} transactionData The membership price.
 * @param {object} transactionData The membership id.
 */
export function trackMembershipPurchase( purchaseId, membershipName, membershipPrice, membershipId ) {
  const fbParams = {
    order_id: `${purchaseId}`,
    content_type: 'product',
    currency: 'USD',
    value: membershipPrice,
    contents: [
      {
        id: `${membershipId}`,
        quantity: 1,
        item_price: membershipPrice,
      },
    ],
  }
  fbSendEvent( 'Purchase', fbParams )

  const ecommerceObj = {
    'purchase': {
      'actionField': {
        'id': `${purchaseId}`,
        'affiliation': 'stretchdspace website',
        'revenue': `${membershipPrice }`,
        'tax': '0.00',
        'shipping': '0.00',
        'coupon': ''
      },
      'products': [{
        'name': membershipName,
        'id': `${membershipId}`,
        'price': `${membershipPrice }`,
        'brand': '',
        'category': 'membership',
        'variant': '',
        'quantity': 1,
      }],
    },
  }
  const dataObj = {
    event: 'mbEvent',
    event_label: 'Membership Purchase',
    event_category: 'Ecommerce',
    event_action: 'purchase',
    event_value: membershipPrice,
    ecommerce: ecommerceObj,
  }
  gtagdatalayerPush( dataObj )
}

/**
 * Send Package purchased event.
 *
 * @param {object} transactionData The package purchase transaction data.
 */
export function trackPackagePurchase( transactionData ) {
  const fbParams = {
    order_id: `${transactionData.cartId}`,
    content_type: 'product',
    currency: 'USD',
    value: transactionData.grandtotal,
    contents: [
      {
        id: `${transactionData.packageId}`,
        quantity: 1,
        item_price: `${transactionData.grandtotal}`,
      },
    ],
  }
  fbSendEvent( 'Purchase', fbParams )
 
  const ecommerceObj = {
    'purchase': {
      'actionField': {
        'id': transactionData.cartId,
        'affiliation': 'stretchdspace website',
        'revenue': `${transactionData.grandtotal}`,
        'tax': `${transactionData.taxtotal}`,
        'shipping': '0.00',
        'coupon': ''
      },
      'products': [{
        'name': transactionData.package,
        'id': `${transactionData.packageId}`,
        'price': `${transactionData.grandtotal - transactionData.taxtotal}`,
        'brand': '',
        'category': 'package',
        'variant': '',
        'quantity': 1,
      }],
    },
  }
  const dataObj = {
    event: 'mbEvent',
    event_label: 'Package Purchase',
    event_category: 'Ecommerce',
    event_action: 'purchase',
    event_value: transactionData.grandtotal,
    ecommerce: ecommerceObj,
  }
  gtagdatalayerPush( dataObj )
}

/**
 * Send Service purchased event.
 *
 * @param {object} serviceData The service data.
 * @param {object} enhancementData The enhancement data.
 * @param {object} transactionData The transaction data.
 */
export function trackServicePurchase( serviceData, enhancementData, transactionData ) {
  const purchasedItems = [{
    id: `${serviceData.id}`,
    quantity: parseInt( serviceData.quantity ),
    item_price: parseFloat( serviceData.price ),
  }]
  _.forEach( enhancementData, item => {
    purchasedItems.push({
      id: `${item.id}`,
      quantity: parseInt( item.quantity ),
      item_price: parseFloat( item.price ),
    })
  })

  const purchaseParams = {
    order_id: `${transactionData.cartId}`,
    content_type: 'product',
    currency: 'USD',
    value: transactionData.grandtotal,
    contents: purchasedItems,
  }
  fbSendEvent( 'Purchase', purchaseParams )

  const ecommerceObj = {
    'purchase': {
      'actionField': {
        'id': `${transactionData.cartId}`,
        'affiliation': 'stretchdspace website',
        'revenue': `${transactionData.grandtotal}`,
        'tax': `${transactionData.taxtotal}`,
        'shipping': '0',
        'coupon': transactionData.coupon,
      },
      'products': [
        serviceData,
        ...enhancementData,
      ],
    },
  }
  const dataObj = {
    event: 'mbEvent',
    event_label: 'Service Purchase',
    event_category: 'Ecommerce',
    event_action: 'purchase',
    event_value: transactionData.grandtotal,
    ecommerce: ecommerceObj,
  }
  gtagdatalayerPush( dataObj )
}

/**
 * Send appointment booked event.
 *
 * @param {string} serviceName The service name.
 * @param {float} servicePrice The service price.
 * @param {array} enhancements The enhancements.
 * @param {string} appointment The appointment schedule.
 * @param {string} staff The staff name.
 */
export function trackAppointmentBooked( serviceName, servicePrice, enhancements, appointment, staff ) {
  const scheduleParams = {
    appointment: appointment,
    staff:staff,
    service: serviceName,
    enhancements: _.map( enhancements, itm => itm.name ),
  }
  fbSendEvent( 'Schedule', scheduleParams )

  scheduleParams.event = 'schedule'
  const dataObj = {
    event: 'mbEvent',
    event_label: 'Schedule',
    event_category: 'MindBody',
    event_action: 'schedule',
    event_value: servicePrice,
    schedule: scheduleParams,
  }
  gtagdatalayerPush( dataObj )
}

/**
 * Send Gift Card  link click event.
 *
 * @param {string} cardName The card name.
 */
export function trackGiftCardButton( cardName ) {
  fbSendEvent( 'InitiateCheckout', {
    content_category: 'giftcard',
    content_name: cardName,
  })

  const dataObj = {
    event: 'mbEvent',
    event_label: 'Giftcard',
    event_category: 'MindBody',
    event_action: 'giftcardRedirect',
    event_value: {
      giftcard: cardName,
    },
  }
  gtagdatalayerPush( dataObj )
}

/**
 * Send Contact event.
 *
 * @param {string} formName The form name.
 */
export function trackFormSubmit( formName ) {
  fbSendEvent( 'Contact', {
    content_name: formName,
  })
}

/**
 * Track contact form submited.
 */
export function setupTrackFormSubmit() {
  const confirmMessage = document.querySelector( '.js-gform-wrapper .gform_confirmation_wrapper' )
  if ( confirmMessage ) {
    const formWrap = findParentElement( confirmMessage, 'js-gform-wrapper' )
    trackFormSubmit( formWrap.dataset.formName )
  } 
}

/**
 * Clean the _wp_http_referer field from a form.
 * 
 * @param {object} formName The form DOM object.
 */
export function clearFormReferer( form ) {
  if ( !form ) {
    return
  }

  const form_referer = form.querySelector( '[name=_wp_http_referer]' )
  if ( form_referer ) {
    form_referer.remove()
  }
}

/**
 * Get a text representation of a date for display.
 *
 * @param {Date} date Date object to get text.
 * @return {string} Date text.
 */
export function dateScheduleDisplay( date ) {
  const month = date.toLocaleString( 'default', { month: 'long' })
  const day = date.toLocaleString( 'default', { day: 'numeric' })
  const dayName = date.toLocaleString( 'default', { weekday: 'long' })
  const year = date.toLocaleString( 'default', { year: 'numeric' })

  return `${dayName} ${month} ${day}, ${year}`
}

/**
 * Get a text representation of a date for use on the Mindbody API.
 *
 * @param {Date} date Date object to get text.
 * @return {string} Date formatted for the Mindbody API.
 */
export function dateApi( date ) {
  const year = date.getFullYear()
  const month = ( date.getMonth() + 1 ).toString().padStart( 2, '0' )
  const day = ( date.getDate() ).toString().padStart( 2, '0' )

  return `${year}/${month}/${day}`
}

/**
 * Gets the time of the Date object formatted for display.
 * @param {Date} date Date object.
 * @return {string} The date's formated time.
 */
export function formatAmPm( date ) {
  var hours = date.getHours()
  var minutes = date.getMinutes()
  var ampm = hours >= 12 ? 'pm' : 'am'
  hours %= 12
  hours = hours ? hours : 12
  minutes = minutes < 10 ? `0${minutes}` : minutes

  return `${hours}:${minutes} ${ampm}`
}

/**
 * Function that validates the returned modal object, if it is a string the modal is not valid
 * and the message error is added to the form.
 *
 * @param {object} relatedForm Form with related booking information.
 * @param {object} modal Modal object to validate.
 * @returns {boolean} Is it a valid modal?
 */
export function validateModal( relatedForm, modal ) {
  relatedForm.classList.remove( 'loading' )
  if ( typeof modal === 'string' || modal instanceof String ) {
    //create clean form errors helepr-function 
    const errors = relatedForm.querySelectorAll( '.js-form-error' )
    for ( let e = 0; e < errors.length; e++ ) {
      errors[e].remove()
    }

    const btnErr = relatedForm.querySelector( 'button.js-disabled' )
    if ( btnErr ) {
      btnErr.classList.remove( 'js-disabled' )
      addFormError( btnErr.parentElement, modal )
    }
    return false
  }

  return true
}

/**
 * 
 * @param {Element} form The form to append input to.
 * @param {string} value The input value.
 * @param {string} name The input name.
 * @param {string} type The input type.
 */
 export function appendToForm( form, value, name, type ) {
  const input = document.createElement( 'input' )
  input.type = type
  input.name = name
  input.value = value
  form.appendChild( input );
}
